// レイヤーレジストリ（来歴 / lineage 管理）。
//
// 役割: 各レイヤー（= DuckDB テーブル）のメタデータを専用テーブル layer_registry に
//       記録する（plan.md §3.2）。これにより (a) エージェントが既存レイヤー一覧を
//       コンテキストとして参照でき、(b) origin_sql から再実行・lineage 追跡ができる。
// 関係: db.js の query() を使用。tools/register-tools.js の list_layers / run_spatial_sql /
//       import_data / drop_layer ハンドラと、手動取込フローがここを更新・参照する。
//
// 列構成（plan.md §3.2）:
//   layer_id TEXT / display_name TEXT / geom_type TEXT / feature_count INTEGER /
//   source_layers TEXT[] / origin_prompt TEXT / origin_sql TEXT / created_at TIMESTAMP
import { query } from './db'

// SQL 文字列リテラル用のエスケープ（NULL は SQL の NULL に）。
function sqlStr(value) {
  if (value == null) return 'NULL'
  return `'${String(value).replace(/'/g, "''")}'`
}

// 文字列配列を DuckDB の TEXT[] リテラルに変換する。
function sqlTextArray(values) {
  if (!values || values.length === 0) return "[]::TEXT[]"
  return `[${values.map(sqlStr).join(', ')}]`
}

// layer_registry テーブルを作成する（存在しなければ）。
// DuckDB 起動後・最初のレイヤー登録前に一度呼ぶ。
export async function ensureRegistry() {
  await query(`
    CREATE TABLE IF NOT EXISTS layer_registry (
      layer_id      TEXT PRIMARY KEY,
      display_name  TEXT,
      geom_type     TEXT,
      feature_count INTEGER,
      source_layers TEXT[],
      origin_prompt TEXT,
      origin_sql    TEXT,
      created_at    TIMESTAMP DEFAULT current_timestamp
    );
  `)
}

// レイヤーを登録（または同一 layer_id を上書き）する。
// fields: { layerId, displayName?, geomType?, featureCount?, sourceLayers?, originPrompt?, originSql? }
export async function registerLayer({
  layerId,
  displayName,
  geomType,
  featureCount = 0,
  sourceLayers = [],
  originPrompt,
  originSql,
}) {
  await ensureRegistry()
  // created_at は current_timestamp を採用（再現性のため記録する）。
  await query(`
    INSERT OR REPLACE INTO layer_registry
      (layer_id, display_name, geom_type, feature_count, source_layers, origin_prompt, origin_sql, created_at)
    VALUES (
      ${sqlStr(layerId)},
      ${sqlStr(displayName ?? layerId)},
      ${sqlStr(geomType)},
      ${Number(featureCount) || 0},
      ${sqlTextArray(sourceLayers)},
      ${sqlStr(originPrompt)},
      ${sqlStr(originSql)},
      current_timestamp
    );
  `)
}

// レジストリの全レイヤーを作成日時順で返す。
export async function listLayers() {
  await ensureRegistry()
  return query(`
    SELECT layer_id, display_name, geom_type, feature_count,
           source_layers, origin_prompt, origin_sql, created_at
    FROM layer_registry
    ORDER BY created_at;
  `)
}

// 単一レイヤーのメタデータを返す（無ければ null）。
export async function getLayer(layerId) {
  await ensureRegistry()
  const rows = await query(`
    SELECT layer_id, display_name, geom_type, feature_count,
           source_layers, origin_prompt, origin_sql, created_at
    FROM layer_registry
    WHERE layer_id = ${sqlStr(layerId)};
  `)
  return rows[0] ?? null
}

// レジストリ行を削除する（テーブル本体の DROP は呼び出し側の責務）。
export async function removeLayer(layerId) {
  await ensureRegistry()
  await query(`DELETE FROM layer_registry WHERE layer_id = ${sqlStr(layerId)};`)
}
